import classNames from '@/common/utils/classNames';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import type { CSSProperties } from 'react';

import { DsButton, DsCard, ds } from '../designSystem';
import { useNativePushPrompt, type NativePushOutcome } from '../hooks/useNativePushPrompt';
import { sendDoctorHomeEvent } from '../utils/analytics';
import { DsDrawer } from './DsDrawer';

export const NATIVE_PUSH_FEATURE = 'doctor-home-native-push';

/** Doctor-specific copy; other places (patient after booking, …) write their own. */
const COPY = {
  bannerTitle: 'نوبت تازه و پیام بیمار را از دست ندهید',
  bannerCaption: 'همان لحظه روی گوشی خبردار شوید',
  bannerCta: 'روشن کردن',
  granted: {
    title: 'اعلان‌ها روشن شد',
    body: 'از این به بعد نوبت تازه، پیام بیمار و نظر جدید را همان لحظه روی گوشی می‌بینید.',
    cta: 'متوجه شدم',
  },
  denied: {
    title: 'اعلان‌ها خاموش ماند',
    body: 'بدون اعلان، نوبت تازه و پیام بیمار را فقط وقتی اپ را باز کنید می‌بینید.',
    cta: 'روشن کردن اعلان‌ها',
    later: 'بعداً',
  },
} as const;

/**
 * Doctor home: banner that turns on the Android app's native notifications, then a sheet with
 * the outcome. Shown only inside an app build that supports it, behind a GrowthBook flag.
 */
export const EnableNotificationsCard = ({ className }: { className?: string }) => {
  const isOn = useFeatureIsOn(NATIVE_PUSH_FEATURE);
  const userId = useUserInfoStore(state => state.info?.id);
  const { canAsk, outcome, enable, dismissOutcome } = useNativePushPrompt({
    onEvent: (name, data) => sendDoctorHomeEvent(userId, name as Parameters<typeof sendDoctorHomeEvent>[1], data),
  });

  if (!isOn) return null;

  return (
    <>
      {canAsk && (
        <DsCard padding="sm" onClick={enable} ariaLabel={COPY.bannerTitle} className={className}>
          <div className={classNames('flex items-center', ds.layout.listRowGap)}>
            <span className={classNames(ds.icon.containerCircle, 'h-10 w-10')}>
              <SolidBell className="h-5 w-5 origin-top animate-p24-bell-nudge motion-reduce:animate-none" />
            </span>
            <div className="min-w-0 flex-1">
              <p className={classNames(ds.type.cardTitle, 'leading-6 [text-wrap:balance]')}>{COPY.bannerTitle}</p>
              <p className={classNames(ds.type.caption, 'mt-0.5')}>{COPY.bannerCaption}</p>
            </div>
            <span className={classNames(ds.type.pillButton, 'shrink-0 whitespace-nowrap')}>{COPY.bannerCta}</span>
          </div>
        </DsCard>
      )}
      <NotificationsOutcomeSheet
        outcome={outcome}
        onClose={dismissOutcome}
        onRetry={() => {
          dismissOutcome();
          enable();
        }}
      />
    </>
  );
};

const NotificationsOutcomeSheet = ({
  outcome,
  onClose,
  onRetry,
}: {
  outcome: NativePushOutcome | null;
  onClose: () => void;
  onRetry: () => void;
}) => {
  const granted = outcome === 'granted';
  const copy = granted ? COPY.granted : COPY.denied;
  return (
    <DsDrawer
      open={outcome !== null}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      title={copy.title}
    >
      {outcome && (
        <div className="flex flex-col items-center px-6 pb-2 pt-6 text-center">
          <BellAnimation granted={granted} />
          <p className="mt-5 text-lg font-bold text-slate-800">{copy.title}</p>
          <p className={classNames(ds.type.caption, 'mt-2 max-w-[18rem] text-sm leading-6 [text-wrap:balance]')}>{copy.body}</p>
          <div className="mt-8 flex w-full flex-col gap-1">
            {granted ? (
              <DsButton onClick={onClose} className="h-12 w-full">
                {COPY.granted.cta}
              </DsButton>
            ) : (
              <>
                <DsButton onClick={onRetry} className="h-12 w-full">
                  {COPY.denied.cta}
                </DsButton>
                <DsButton variant="text" onClick={onClose} className="h-12 w-full">
                  {COPY.denied.later}
                </DsButton>
              </>
            )}
          </div>
        </div>
      )}
    </DsDrawer>
  );
};

/** Filled bell (the outline icon looked thin at this size). */
const SolidBell = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2.5a6.5 6.5 0 0 0-6.5 6.5v3.6l-1.7 2.9A1 1 0 0 0 4.7 17h14.6a1 1 0 0 0 .9-1.5l-1.7-2.9V9A6.5 6.5 0 0 0 12 2.5Z" />
    <path d="M9.2 18.5a2.9 2.9 0 0 0 5.6 0H9.2Z" />
  </svg>
);

const CONFETTI_COLORS = ['bg-primary', 'bg-emerald-500', 'bg-amber-400', 'bg-pink-500', 'bg-secondary'];
const CONFETTI = Array.from({ length: 14 }, (_, i) => {
  const angle = (i / 14) * Math.PI * 2;
  const r = 64 + (i % 3) * 10;
  return { x: Math.round(Math.cos(angle) * r), y: Math.round(Math.sin(angle) * r), color: CONFETTI_COLORS[i % 5], round: i % 2 === 0 };
});

/** Granted: a grey bell lights up blue, rings and throws confetti. Denied: a muted, crossed-out bell. */
const BellAnimation = ({ granted }: { granted: boolean }) => (
  <div className="relative flex h-32 w-32 items-center justify-center" aria-hidden>
    {granted &&
      CONFETTI.map((c, i) => (
        <span
          key={i}
          className={classNames(
            'absolute h-2 w-2 animate-p24-confetti motion-reduce:hidden',
            c.color,
            c.round ? 'rounded-full' : 'rounded-[2px]',
          )}
          style={{ '--p24-x': `${c.x}px`, '--p24-y': `${c.y}px` } as CSSProperties}
        />
      ))}
    <span
      className={classNames(
        'relative flex h-20 w-20 items-center justify-center rounded-full',
        granted
          ? 'animate-p24-bell-light-up motion-reduce:animate-none motion-reduce:bg-primary motion-reduce:text-white'
          : 'bg-slate-100 text-slate-400',
      )}
    >
      <SolidBell
        className={classNames(
          'h-10 w-10 origin-top motion-reduce:animate-none',
          granted ? 'animate-p24-bell-ring' : 'animate-p24-bell-muted',
        )}
      />
      {!granted && <span className="absolute h-1 w-12 rotate-45 rounded-full bg-slate-400 ring-2 ring-slate-100" />}
    </span>
  </div>
);
